import {
  BaseQueryFn,
  createApi,
  fetchBaseQuery,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { captureExceptionWithRecording } from '@whitelabel/helpers/utils';
import { CP_ROOT } from '../../helpers/constants';
import { getRCAuthToken } from '../../helpers/auth';

type ExtraOptions = {
  isPublic?: boolean;
};

const baseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  ExtraOptions,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  const rawBaseQuery = fetchBaseQuery({
    prepareHeaders: async (headers, apiArg) => {
      // Add root to both XC & RC
      headers.set('X-CP-ROOT', CP_ROOT);
      headers.set('X-Client-Timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);

      // Skip authentication for public endpoints
      if (extraOptions?.isPublic) {
        return headers;
      }

      if (CP_ROOT === 'xcover') {
        try {
          const session = await fetchAuthSession();
          const jwtToken = session.tokens?.accessToken?.toString();
          if (jwtToken) {
            headers.set('Authorization', `Bearer ${jwtToken}`);
          } else {
            throw new Error(`No token attached to request ${apiArg.endpoint}`);
          }
        } catch (error: any) {
          if (error?.name !== 'UserUnAuthenticatedException') captureExceptionWithRecording(new Error(error));
        }
      }
      if (CP_ROOT === 'rentalcover') {
        try {
          const token = getRCAuthToken();
          if (token) {
            headers.set('Authorization', `Bearer ${token}`);
          } else {
            throw new Error(`No token attached to request ${apiArg.endpoint}`);
          }
        } catch (error: any) {
          captureExceptionWithRecording(new Error(error));
        }
      }
      return headers;
    },
  });

  return rawBaseQuery(args, api, extraOptions);
};

// Define a service using a base URL and expected endpoints
export const claimsPortalAPI = createApi({
  reducerPath: 'claimsPortalAPI',
  baseQuery,
  tagTypes: ['details', 'RCToken'],
  endpoints: () => ({}),
});
